<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import {
		AdvancedCardV2,
		AlertV2,
		ActionsContainer,
		ButtonV2,
		Container,
		IconButton,
		InputV2,
		Modal,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import IRLearnCodeModal from './IRLearnCodeModal.svelte';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		deleteIRCode,
		emitIRCode,
		getIRCodes,
		setIRCodeName,
		type IRCode,
	} from 'src/helpers/ir_controller_helpers';

	export let device: Device;
	export let irDeviceId: number;

	let waitForResponse = false;
	let showLearnModal = false;
	let showDeleteCodeConfirm = false;
	let showEditCodeModal = false;
	let codeToDelete: IRCode | null = null;
	let codeToEdit: IRCode | null = null;
	let editCodeName = '';
	let learnInitialName = '';
	let emittingId: number | null = null;

	$: settingsStore = getDeviceSettings(device?.id);
	$: statusStore = getDeviceStatus(device?.id);
	$: settings = $settingsStore;
	$: status = $statusStore;

	$: irCodes = getIRCodes(settings, irDeviceId);

	const handleEmitCode = async (codeId: number) => {
		if (emittingId !== null || !status?.online) return;
		emittingId = codeId;
		const response: SettingRequest = await emitIRCode(device, codeId);
		if (response?.isok) {
			toast.open({
				type: 'success',
				title: $format('ir_controller_code_emitted', { default: 'Code emitted' }),
			});
		} else {
			onSettingsResponse(response, device);
		}
		emittingId = null;
	};

	const handleOpenLearn = () => {
		learnInitialName = '';
		showLearnModal = true;
	};

	const handleOpenRelearn = () => {
		learnInitialName = codeToEdit?.name || '';

		showEditCodeModal = false;
		showLearnModal = true;
	};

	const handleOpenEditCode = (code: IRCode) => {
		codeToEdit = code;
		editCodeName = code.name;
		showEditCodeModal = true;
	};

	const handleCloseEditCode = () => {
		showEditCodeModal = false;
		codeToEdit = null;
	};

	const handleSaveCode = async () => {
		const trimmedName = editCodeName.trim();
		if (!codeToEdit || !trimmedName || waitForResponse) return;
		if (trimmedName === codeToEdit.name) {
			handleCloseEditCode();
			return;
		}
		if (!status?.online) {
			onSettingsResponse({ isok: false }, device);
			return;
		}
		waitForResponse = true;
		const response: SettingRequest = await setIRCodeName(
			device,
			codeToEdit.id,
			trimmedName,
			irDeviceId,
		);
		onSettingsResponse(response, device);
		waitForResponse = false;
		if (response?.isok) handleCloseEditCode();
	};

	const handleOpenDeleteCode = (code: IRCode) => {
		codeToDelete = code;
		showDeleteCodeConfirm = true;
	};

	const handleCloseDeleteCode = () => {
		showDeleteCodeConfirm = false;
		codeToDelete = null;
	};

	const handleDeleteCode = async () => {
		if (!codeToDelete || waitForResponse) return;
		waitForResponse = true;
		const response: SettingRequest = await deleteIRCode(device, irDeviceId, codeToDelete.name);
		onSettingsResponse(response, device, {
			success: $format('ir_controller_delete_code_success', {
				default: 'IR code deleted.',
			}),
		});
		waitForResponse = false;
		if (response?.isok) handleCloseDeleteCode();
	};
</script>

<div class="d-flex align-items-center justify-content-between mb-s gap-s">
	<Text styleType="h2">
		{$format('ir_controller_learned_codes', { default: 'Learned codes' })}
	</Text>
	<ButtonV2
		size="s"
		variant="secondary"
		icon="fas fa-plus"
		disabled={!status?.online}
		aria-label={$format('ir_controller_learn_code', { default: 'Learn code' })}
		on:click={handleOpenLearn}
	>
		{$format('ir_controller_learn_code', { default: 'Learn code' })}
	</ButtonV2>
</div>
{#if irCodes.length === 0}
	<AlertV2
		class="mb-s"
		isHeadingBold
		heading={$format('ir_controller_no_codes', { default: 'No codes learned yet' })}
	/>
{:else}
	{#each irCodes as code (code.key)}
		<AdvancedCardV2 class="mb-s" title={code.name} isFocusable={false}>
			<ActionsContainer type="simple" slot="right">
				<IconButton
					slot="first"
					variant="help-2"
					icon="fas fa-play"
					disabled={!status?.online || emittingId !== null}
					aria-label={$format('ir_controller_send_code', {
						values: { name: code.name },
						default: `Send ${code.name}`,
					})}
					on:click={() => handleEmitCode(code.id)}
				/>
				<IconButton
					slot="second"
					variant="help-2"
					icon="fas fa-pen"
					disabled={!status?.online || waitForResponse}
					aria-label={$format('ir_controller_edit_code', {
						values: { name: code.name },
						default: `Edit ${code.name}`,
					})}
					on:click={() => handleOpenEditCode(code)}
				/>
				<IconButton
					slot="third"
					variant="help-2"
					icon="fas fa-trash"
					disabled={!status?.online || waitForResponse}
					aria-label={$format('ir_controller_delete_code', {
						values: { name: code.name },
						default: `Delete ${code.name}`,
					})}
					on:click={() => handleOpenDeleteCode(code)}
				/>
			</ActionsContainer>
		</AdvancedCardV2>
	{/each}
{/if}

<IRLearnCodeModal
	{device}
	{irDeviceId}
	irCodesCount={irCodes.length}
	initialName={learnInitialName}
	bind:showModal={showLearnModal}
/>

<Modal
	showModal={showDeleteCodeConfirm}
	title={$format('ir_controller_delete_ir_code', { default: 'Delete IR code' })}
	on:close={handleCloseDeleteCode}
>
	<Container slot="content" padding="0">
		<Text>
			{$format('ir_controller_delete_ir_code_confirm', {
				values: { name: codeToDelete?.name || '' },
				default: `Are you sure you want to delete ${codeToDelete?.name || 'this IR code'}?`,
			})}
		</Text>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			size="m"
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={handleCloseDeleteCode}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			size="m"
			variant="danger"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			loading={waitForResponse}
			aria-label={$format('button_delete', { default: 'Delete' })}
			on:click={handleDeleteCode}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showEditCodeModal}
	title={$format('ir_controller_edit_ir_code', { default: 'Edit IR code' })}
	on:close={handleCloseEditCode}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<div class="d-flex align-items-center justify-content-end mb-s">
			<ButtonV2
				size="s"
				variant="secondary"
				disabled={!status?.online}
				aria-label={$format('ir_controller_relearn_command', { default: 'Relearn command' })}
				on:click={handleOpenRelearn}
			>
				{$format('ir_controller_relearn_command', { default: 'Relearn command' })}
			</ButtonV2>
		</div>
		<InputV2
			id="ir-controller-edit-code-name"
			size="m"
			label={$format('ir_controller_code_name', { default: 'Command name' })}
			aria-label={$format('ir_controller_code_name', { default: 'Command name' })}
			icon={null}
			maxlength="255"
			bind:value={editCodeName}
		/>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			size="m"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			on:click={handleCloseEditCode}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			size="m"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			loading={waitForResponse}
			disabled={waitForResponse || !editCodeName.trim()}
			aria-label={$format('button_save', { default: 'Save' })}
			on:click={handleSaveCode}
		>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>
